import * as THREE from 'three';
import { MeshBVHUniformStruct, shaderIntersectFunction, shaderStructs } from 'three-mesh-bvh';

export class DiamondShaderMaterial extends THREE.ShaderMaterial {
  constructor(envrionment: THREE.Texture, camera: THREE.Camera) {
    super({
      uniforms: {
        envMap: { value: envrionment },
        correctMips: { value: false },
        fresnel: { value: 200 },
        bvh: { value: new MeshBVHUniformStruct() },
        projectionMatrixInverse: { value: camera.projectionMatrixInverse },
        viewMatrixInverse: { value: camera.matrixWorld },
        resolution: { value: new THREE.Vector2(1000, 1000) },
        opacity: { value: 1.0 },
        // internal reflection settings
        bounces: { value: 3 },
        ior: { value: 2.4 },

        // chroma and color settings
        color: { value: new THREE.Color(0.4, 0.4, 0.4) },
        fastChroma: { value: false },
        aberrationStrength: { value: 0.03 },
      },
      defines: {
        "CHROMATIC_ABERRATIONS": true,
      },
      vertexShader: `
       uniform mat4 viewMatrixInverse;

        varying vec3 vWorldPosition;
        varying vec3 vNormal;
        varying mat4 vModelMatrixInverse;

        #include <color_pars_vertex>

        void main() {
            #include <color_vertex>

            vec4 transformedNormal = vec4(normal, 0.0);
            vec4 transformedPosition = vec4(position, 1.0);
            #ifdef USE_INSTANCING
            transformedNormal = instanceMatrix * transformedNormal;
            transformedPosition = instanceMatrix * transformedPosition;
            #endif

            #ifdef USE_INSTANCING
            vModelMatrixInverse = inverse(modelMatrix * instanceMatrix);
            #else
            vModelMatrixInverse = inverse(modelMatrix);
            #endif

            vWorldPosition = (modelMatrix * transformedPosition).xyz;
            vNormal = normalize((viewMatrixInverse * vec4(normalMatrix * transformedNormal.xyz, 0.0)).xyz);
            gl_Position = projectionMatrix * viewMatrix * modelMatrix * transformedPosition;
        }
    `,
      fragmentShader: `
            #define ENVMAP_TYPE_CUBE_UV
			precision highp isampler2D;
			precision highp usampler2D;
			varying vec3 vWorldPosition;
			varying vec3 vNormal;
			varying mat4 vModelMatrixInverse;

			#include <color_pars_fragment>

			#ifdef ENVMAP_TYPE_CUBEM
			  uniform samplerCube envMap;
			#else
			  uniform sampler2D envMap;
			#endif

			uniform float bounces;
			// ${shaderStructs}
			// ${shaderIntersectFunction}
			uniform BVH bvh;
			uniform float ior;
			// uniform bool correctMips;
			uniform vec2 resolution;
			uniform float fresnel;
			uniform mat4 modelMatrix;
			uniform mat4 projectionMatrixInverse;
			uniform mat4 viewMatrixInverse;
			uniform float aberrationStrength;
			uniform vec3 color;
			uniform float opacity;

			float fresnelFunc(vec3 viewDirection, vec3 worldNormal) {
			  return max(pow( 1.0 + dot( viewDirection, worldNormal), 10.0 ), 0.0);
			}

			vec3 totalInternalReflection(vec3 ro, vec3 rd, vec3 normal, float ior, mat4 modelMatrixInverse) {
			  vec3 rayOrigin = ro;
			  vec3 rayDirection = rd;
			  rayDirection = refract(rayDirection, normal, 1.0 / ior);
			  rayOrigin = vWorldPosition + rayDirection * 0.001;
			  rayOrigin = (modelMatrixInverse * vec4(rayOrigin, 1.0)).xyz;
			  rayDirection = normalize((modelMatrixInverse * vec4(rayDirection, 0.0)).xyz);
			  for(float i = 0.0; i < bounces; i++) {
				uvec4 faceIndices = uvec4( 0u );
				vec3 faceNormal = vec3( 0.0, 0.0, 1.0 );
				vec3 barycoord = vec3( 0.0 );
				float side = 1.0;
				float dist = 0.0;
				bvhIntersectFirstHit( bvh, rayOrigin, rayDirection, faceIndices, faceNormal, barycoord, side, dist );
				vec3 hitPos = rayOrigin + rayDirection * max(dist - 0.001, 0.0);
				vec3 tempDir = refract(rayDirection, faceNormal, ior);
				if (length(tempDir) != 0.0) {
				  rayDirection = tempDir;
				  break;
				}
				rayDirection = reflect(rayDirection, faceNormal);
				rayOrigin = hitPos + rayDirection * 0.01;
			  }
			  rayDirection = normalize((modelMatrix * vec4(rayDirection, 0.0)).xyz);
			  return rayDirection;
			}

			#include <common>
			// #include <cube_uv_reflection_fragment>

			#ifdef ENVMAP_TYPE_CUBEM
			  vec4 textureGradient(samplerCube envMap, vec3 rayDirection, vec3 directionCamPerfect) {
				vec2 dx = dFdx(directionCamPerfect); // dFdx(correctMips ? directionCamPerfect: rayDirection);
				vec2 dy = dFdy(directionCamPerfect); // dFdy(correctMips ? directionCamPerfect: rayDirection);
			  	return textureGrad(envMap, rayDirection, dx, dy);
			  }
			#else
			  vec4 textureGradient(sampler2D envMap, vec3 rayDirection, vec3 directionCamPerfect) {
				vec2 uvv = equirectUv( rayDirection );
				vec2 smoothUv = equirectUv( directionCamPerfect );
				vec2 dx = dFdx(smoothUv); // dFdx(correctMips ? smoothUv : uvv);
				vec2 dy = dFdy(smoothUv); // dFdy(correctMips ? smoothUv : uvv);
				return textureGrad(envMap, uvv, dx, dy);
			  }
			#endif

			void main() {
			  vec2 uv = gl_FragCoord.xy / resolution;

			  vec3 directionCamPerfect = (projectionMatrixInverse * vec4(uv * 2.0 - 1.0, 0.0, 1.0)).xyz;
			  directionCamPerfect = (viewMatrixInverse * vec4(directionCamPerfect, 0.0)).xyz;
			  directionCamPerfect = normalize(directionCamPerfect);
			  vec3 normal = vNormal;
			  vec3 rayOrigin = cameraPosition;
			  vec3 rayDirection = normalize(vWorldPosition - cameraPosition);

			  vec4 diffuseColor = vec4(color, opacity);
			  #include <color_fragment>

			  #ifdef CHROMATIC_ABERRATIONS
				vec3 rayDirectionG = totalInternalReflection(rayOrigin, rayDirection, normal, max(ior, 1.0), vModelMatrixInverse);
				#ifdef FAST_CHROMA
				  vec3 rayDirectionR = normalize(rayDirectionG + 1.0 * vec3(aberrationStrength / 2.0));
				  vec3 rayDirectionB = normalize(rayDirectionG - 1.0 * vec3(aberrationStrength / 2.0));
				#else
				  vec3 rayDirectionR = totalInternalReflection(rayOrigin, rayDirection, normal, max(ior * (1.0 - aberrationStrength), 1.0), vModelMatrixInverse);
				  vec3 rayDirectionB = totalInternalReflection(rayOrigin, rayDirection, normal, max(ior * (1.0 + aberrationStrength), 1.0), vModelMatrixInverse);
				#endif
				float finalColorR = textureGradient(envMap, rayDirectionR, directionCamPerfect).r;
				float finalColorG = textureGradient(envMap, rayDirectionG, directionCamPerfect).g;
				float finalColorB = textureGradient(envMap, rayDirectionB, directionCamPerfect).b;
				diffuseColor.rgb *= vec3(finalColorR, finalColorG, finalColorB);
			  #else
				rayDirection = totalInternalReflection(rayOrigin, rayDirection, normal, max(ior, 1.0), vModelMatrixInverse);
				diffuseColor.rgb *= textureGradient(envMap, rayDirection, directionCamPerfect).rgb;
			  #endif

			  vec3 viewDirection = normalize(vWorldPosition - cameraPosition);

			  float nFresnel = fresnelFunc(viewDirection, normal) * fresnel;
			  gl_FragColor = vec4(mix(diffuseColor.rgb, vec3(1.0), nFresnel), 1);

			  #include <tonemapping_fragment>
			  #include <colorspace_fragment>
			}
    `,
    });
  }
}
